import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Options, { OptionType } from './Options';
import styles from './RCUISingleSelect.scss';
import Icon from './Icon';

class RCUISingleSelect extends Component {
  static getSelectOptionIndex(options, selectedOption) {
    if (!selectedOption) return 0;
    return findIndex(options, o => o.displayText === selectedOption.displayText);
  }

  constructor(props) {
    super(props);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.selectHandler = this.selectHandler.bind(this);
    this.keyDownHandler = this.keyDownHandler.bind(this);
  }

  state = {
    isOpen: false,
  }

  componentDidMount() {
    document.addEventListener('click', this.closeDropdown);
  }

  componentDidUpdate() {
    if (this.props.onUpdate) {
      const { isOpen } = this.state;
      const optionsDOM = isOpen ? this.optionsListRef.querySelector('ul') : null; // set the open options panel DOM as target for the event
      this.props.onUpdate({ target: optionsDOM }); // call onUpdate listener
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.closeDropdown);
  }

  closeDropdown(event) {
    let node = event && event.target;
    while (node && (node !== this.selectContainer)) {
      node = node.parentNode;
    }
    if (!node) {
      this.setState({ isOpen: false });
    }
  }

  toggleDropdown() {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  }

  selectHandler(option) {
    const { selectedOption } = this.props;
    this.closeDropdown();
    this.selectButton.focus();
    if (!isEqual(selectedOption, option)) {
      this.props.onChange(option);
    }
  }

  keyDownHandler(event) {
    switch (event.keyCode) {
      case 27: // Esc key
        this.closeDropdown();
        this.selectButton.focus();
        break;
      default: break;
    }
  }

  preRender() {
    const { placeholder, selectedOption, customRenderer } = this.props;
    if (customRenderer) {
      return selectedOption ? customRenderer(selectedOption) : placeholder;
    }
    return selectedOption ? selectedOption.displayText : placeholder;
  }

  populateSelectButtonClassName() {
    const { selectButtonClassName, alignment, options, selectedOption } = this.props;

    // Decides between blue state and grey state of button - If the first option is the selected option... Assume the user has not interacted with the refinements
    const selectedOptionIndex = RCUISingleSelect.getSelectOptionIndex(options, selectedOption);
    const selectButtonSelected = selectedOptionIndex > 0 ? styles.selectButtonSelected : '';

    return `${styles.selectButton} ${selectButtonSelected} ${selectButtonClassName || ''} ${styles[alignment] || ''}`;
  }

  populateArrowClassName() {
    const { isOpen } = this.state;
    const { arrowDownClassName, arrowUpClassName } = this.props;

    return isOpen ? arrowUpClassName || styles.arrowIcon : arrowDownClassName || styles.arrowIconDown;
  }

  poulateContainerClassName() {
    const { isOpen } = this.state;
    const { containerClassName, containerOpenClassName } = this.props;

    return `${containerClassName} ${isOpen ? containerOpenClassName : ''}`;
  }

  render() {
    const {
      id,
      options,
      selectedOption,
      optionListClassName,
      optionItemClassName,
      optionCustomRenderer,
    } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        ref={(r) => { this.selectContainer = r; }}
        className={this.poulateContainerClassName()}
        onKeyDown={this.keyDownHandler}
      >
        <button
          id={id}
          className={this.populateSelectButtonClassName()}
          onClick={this.toggleDropdown}
          ref={(c) => { this.selectButton = c; }}
          type="button"
        >
          <span>
            { this.preRender() }
          </span>
          <Icon name="chevronRight" className={this.populateArrowClassName()} />
        </button>
        { isOpen &&
          <div ref={(r) => { this.optionsListRef = r; }}>
            <Options
              options={options}
              onSelect={this.selectHandler}
              selectedOption={selectedOption}
              optionListClassName={optionListClassName}
              optionItemClassName={optionItemClassName}
              customRenderer={optionCustomRenderer}
            />
          </div>
        }
      </div>
    );
  }
}

RCUISingleSelect.propTypes = {
  id: PropTypes.string,
  onUpdate: PropTypes.func,
  alignment: PropTypes.oneOf(['left', 'right']),
  options: PropTypes.arrayOf(OptionType).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  selectedOption: OptionType,
  customRenderer: PropTypes.func,
  optionCustomRenderer: PropTypes.func,
  selectButtonClassName: PropTypes.string,
  optionListClassName: PropTypes.string,
  optionItemClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  containerOpenClassName: PropTypes.string,
  arrowDownClassName: PropTypes.string,
  arrowUpClassName: PropTypes.string,
};

RCUISingleSelect.defaultProps = {
  selectButtonClassName: '',
  optionListClassName: styles.optionList,
  optionItemClassName: styles.optionItem,
  containerClassName: styles.container,
  containerOpenClassName: styles.optionsListOpen,
  arrowDownClassName: styles.arrowDown,
  arrowUpClassName: styles.arrowUp,
};

export default RCUISingleSelect;
